import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import {
  OrganizationService,
  OrganizationDto,
  CreateOrganizationRequest,
  ListOrganizationRequest,
  OrganizationMemberRole,
  OrganizationParkerEnrichedDto,
  RegisteredService,
  CreatedRegisteredParkerRequest,
  ParkerService,
  ParkerSiteClassificationService,
  ParkerClassification,
  ClassificationSource,
  ParkingTypeService,
  ListLongTermParkersRequest,
  CreateLongTermParkersRequest,
  ParkingRightService,
  CreateParkingRightRequest,
  TimeBasedParkingRightDto,
  ParkingRightTypeEnum,
} from '../../../api/gen';
import { COUNTRIES, DEFAULT_COUNTRY_CODE } from 'shared-lib';
import { describeHttpError } from '../http-error';
import { DateFieldComponent } from '../date-field/date-field.component';

type RegistrationType = 'Supplier' | 'Employee' | 'Student';

interface MemberRow {
  firstName: string;
  lastName: string;
  /** AK number — only relevant for Employee/Student (JKU), ignored for suppliers. */
  akNumber: string;
  /** Free-text list of plates for this member, separated by comma / space / newline. */
  plates: string;
  /** Optional per-member contact phone. */
  phone: string;
  /**
   * The person's real email, when the operator knows it.
   *
   * Optional, but it is the only thing that connects this record to the person later: their login
   * is matched on email, so an account created under a made-up address is one they can never reach.
   * Left blank, {@link MemberRegistrationComponent.buildTempEmail} generates a placeholder and the
   * operator has to correct it by hand once the real address is known.
   */
  email: string;
}

function emptyMember(): MemberRow {
  return { firstName: '', lastName: '', akNumber: '', plates: '', phone: '', email: '' };
}

@Component({
  selector: 'app-member-registration',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, DateFieldComponent],
  templateUrl: './member-registration.component.html',
})
export class MemberRegistrationComponent {
  // ---- Inputs / outputs -----------------------------------------------------
  /** The site (parking space) the org members are being credentialed for. */
  parkingSpaceId = input<string | null>(null);
  parkingSpaceName = input<string>('');
  /** The organization this parking space belongs to (ParkingSpace.externalOrganizationId), when set. */
  externalOrganizationId = input<string | null>(null);
  /** Zones of the current parking space, for scoping the long-term right (optional). */
  zones = input<{ id?: string; name?: string | null }[]>([]);
  /** The logged-in operator id — attributed to the org, rights, and classification. */
  operatorId = input<string | null>(null);

  /** Emitted after a successful company registration so the parent can refresh. */
  registered = output<void>();

  private orgService = inject(OrganizationService);
  private registeredService = inject(RegisteredService);
  private parkerService = inject(ParkerService);
  private classificationService = inject(ParkerSiteClassificationService);
  private parkingTypeService = inject(ParkingTypeService);
  private parkingRightService = inject(ParkingRightService);
  private snackBar = inject(MatSnackBar);

  // ---- Form state -----------------------------------------------------------
  registrationType = signal<RegistrationType>('Supplier');

  /**
   * True for JKU-internal registrations, which are the only ones that carry an AK number.
   * Gates the AK field and its comment line — NOT the site classification: every
   * registration type gets one, suppliers included (the hotline registry filters on it).
   */
  usesAkNumber = computed(() => this.registrationType() !== 'Supplier');

  companyName = signal('');
  matchedOrg = signal<OrganizationDto | null>(null);
  companyResults = signal<OrganizationDto[]>([]);
  isSearching = signal(false);
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  /**
   * The organization this parking space belongs to, resolved from `ParkingSpace.externalOrganizationId`.
   * Generic by design, not JKU-specific: any site with that field set gets the same auto-select/lock
   * behaviour for Employee/Student registrations. Null when the space carries no organization id, in
   * which case the operator picks a company by hand as before.
   */
  linkedOrg = signal<OrganizationDto | null>(null);

  /** Employee/Student registrations at a linked-organization site always belong to that org. */
  isCompanyLocked = computed(() => this.usesAkNumber() && !!this.linkedOrg());

  constructor() {
    // Re-fetches whenever the parent assigns a different parking space (component is normally
    // created fresh per space, but this also covers same-instance reuse).
    effect(() => {
      const orgId = this.externalOrganizationId();
      if (orgId) {
        void this.loadLinkedOrganization(orgId);
      } else {
        // Nothing to resolve — leave the company field free, as if no org were linked.
        this.linkedOrg.set(null);
      }
    });
  }

  private async loadLinkedOrganization(organizationId: string): Promise<void> {
    try {
      const org = await firstValueFrom(
        this.orgService.organizationsOrganizationIdFetchPost(organizationId, { includeMembers: false }));
      this.linkedOrg.set(org ?? null);
      if (org && this.usesAkNumber()) {
        this.companyName.set(org.name ?? '');
        this.selectOrg(org);
      }
    } catch (e) {
      console.error('Could not resolve the organization linked to this parking space:', e);
      this.linkedOrg.set(null);
    }
  }

  /**
   * Members already registered for the selected company.
   *
   * Fetched rather than read off the search hit: `ListOrganization` returns `parkers`, which is
   * ids and roles only. Names and plates live in the Parker context, and `FetchOrganization` with
   * `IncludeMembers` is what resolves them (`ParkersEnriched`).
   */
  orgMembers = signal<OrganizationParkerEnrichedDto[]>([]);
  isLoadingMembers = signal(false);

  /**
   * Sequence number for member fetches. The company search is debounced and re-runs on every
   * keystroke, so several fetches can be in flight; without this an earlier, slower response would
   * land last and show the wrong company's members.
   */
  private memberFetchToken = 0;

  // Company-level contact info (persisted on the Organization).
  contactPerson = signal('');
  contactPhone = signal('');
  addressLine = signal('');
  addressLocality = signal('');
  addressPostCode = signal('');
  // Country is required by the backend (valid 2-letter ISO code) — default to Austria.
  addressCountry = signal(DEFAULT_COUNTRY_CODE);

  /** ISO 3166-1 alpha-2 country options for the company address dropdown. */
  readonly countries = COUNTRIES;

  validTo = signal('');

  members = signal<MemberRow[]>([emptyMember()]);
  selectedZoneIds = signal<string[]>([]);

  isSubmitting = signal(false);

  // ---- Registration type ----------------------------------------------------
  setType(type: RegistrationType): void {
    this.registrationType.set(type);

    // Employee/Student at a single-organization site: force the company back to that
    // organization — the field is locked in the template, so this is the only way it changes.
    const org = this.linkedOrg();
    if (type !== 'Supplier' && org) {
      this.companyName.set(org.name ?? '');
      this.selectOrg(org);
    }
  }

  // ---- Company search / selection ------------------------------------------
  async searchCompany(): Promise<void> {
    const name = this.companyName().trim();
    if (!name) {
      this.companyResults.set([]);
      this.matchedOrg.set(null);
      return;
    }
    this.isSearching.set(true);
    try {
      const request: ListOrganizationRequest = { name, pageIndex: 0, pageSize: 5, includeDeleted: false };
      const result = await firstValueFrom(this.orgService.organizationsFilterPost(request));
      const items = result?.items ?? [];
      this.companyResults.set(items);
      // Auto-select an exact (case-insensitive) name match as the target org.
      const exact = items.find(o => (o.name ?? '').trim().toLowerCase() === name.toLowerCase());
      this.selectOrg(exact ?? null);
    } catch (e) {
      console.error('Company search failed:', e);
    } finally {
      this.isSearching.set(false);
    }
  }

  useOrg(org: OrganizationDto): void {
    this.selectOrg(org);
    this.companyName.set(org.name ?? '');
    this.companyResults.set([]);
  }

  /** Single entry point for changing the selected company, so its member list never goes stale. */
  private selectOrg(org: OrganizationDto | null): void {
    this.matchedOrg.set(org);
    void this.loadOrgMembers(org?.id ?? null);
  }

  private async loadOrgMembers(organizationId: string | null): Promise<void> {
    const token = ++this.memberFetchToken;
    if (!organizationId) {
      this.orgMembers.set([]);
      this.isLoadingMembers.set(false);
      return;
    }

    this.isLoadingMembers.set(true);
    try {
      const org = await firstValueFrom(
        this.orgService.organizationsOrganizationIdFetchPost(organizationId, { includeMembers: true }));
      if (token !== this.memberFetchToken) return;
      this.orgMembers.set(org?.parkersEnriched ?? []);
    } catch (e) {
      // Showing nothing is honest; showing the previous company's members would not be. The
      // registration form keeps working either way — this panel is informational.
      if (token !== this.memberFetchToken) return;
      console.error('Could not load company members:', e);
      this.orgMembers.set([]);
    } finally {
      if (token === this.memberFetchToken) this.isLoadingMembers.set(false);
    }
  }

  /** Full name, or '' when the member record carries neither part. */
  memberName(member: OrganizationParkerEnrichedDto): string {
    return [member.firstName?.trim(), member.lastName?.trim()].filter(Boolean).join(' ');
  }

  memberPlates(member: OrganizationParkerEnrichedDto): string[] {
    return (member.activeLicenses ?? [])
      .map(licence => licence.fullNumber?.trim())
      .filter((fullNumber): fullNumber is string => !!fullNumber);
  }

  /** Only Admin earns a badge — everyone else is an ordinary member. */
  isOrgAdmin(member: OrganizationParkerEnrichedDto): boolean {
    return member.role === OrganizationMemberRole.Admin;
  }

  // ---- Company details shown with a search hit -------------------------------
  //
  // ListOrganization already returns contact fields, billing address and members (see
  // ListOrganizations: it Includes BillingAddress and Parkers), so these read straight off the
  // search response — do NOT add a per-result fetch here. The dropdown used to print only the
  // name, which made two similarly named companies impossible to tell apart at exactly the moment
  // one has to be picked; that is how duplicate organizations kept being created.
  //
  // Display only: the fields below the search box feed organization *creation*, so they are
  // deliberately not prefilled from a matched org — that would carry one company's contact
  // details into the next new company if the operator hits "Change". Editing an existing
  // company's contact lives in the registry's edit dialog.

  /** "Contact person · phone", or '' when the company has neither stored. */
  orgContact(org: OrganizationDto): string {
    return [org.contactPerson?.trim(), org.contactPhone?.trim()].filter(Boolean).join(' · ');
  }

  /** "Street 4, 4040 Linz (AT)" — each part dropped when missing, so no stray commas. */
  orgAddress(org: OrganizationDto): string {
    const address = org.billingAddress;
    if (!address) return '';
    const cityParts = [address.postCode?.trim(), address.locality?.trim()].filter(Boolean).join(' ');
    const country = address.countryCode?.trim();
    const cityLine = [cityParts, country ? `(${country})` : ''].filter(Boolean).join(' ');
    return [address.firstAddressLine?.trim(), cityLine].filter(Boolean).join(', ');
  }

  /**
   * "3 members", or '' when the response carried no member collection at all — an absent list is
   * unknown, not empty, and rendering "0 members" for it would be a plain lie about the company.
   */
  orgMembersLabel(org: OrganizationDto): string {
    if (!org.parkers) return '';
    const count = org.parkers.length;
    return `${count} member${count === 1 ? '' : 's'}`;
  }

  clearMatchedOrg(): void {
    this.selectOrg(null);
  }

  /** Debounced auto-search as the operator types the company name. */
  onCompanyNameChange(value: string): void {
    this.companyName.set(value);
    this.selectOrg(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.searchCompany(), 300);
  }

  // ---- Member rows ----------------------------------------------------------
  addMember(): void {
    this.members.update(rows => [...rows, emptyMember()]);
  }

  removeMember(index: number): void {
    this.members.update(rows => rows.length > 1 ? rows.filter((_, i) => i !== index) : rows);
  }

  updateMember(index: number, patch: Partial<MemberRow>): void {
    this.members.update(rows => rows.map((r, i) => i === index ? { ...r, ...patch } : r));
  }

  // ---- Zones ----------------------------------------------------------------
  isZoneSelected(zoneId: string | undefined): boolean {
    return !!zoneId && this.selectedZoneIds().includes(zoneId);
  }

  toggleZone(zoneId: string | undefined): void {
    if (!zoneId) return;
    this.selectedZoneIds.update(ids => ids.includes(zoneId) ? ids.filter(id => id !== zoneId) : [...ids, zoneId]);
  }

  // ---- Submit ---------------------------------------------------------------
  async submit(): Promise<void> {
    const spaceId = this.parkingSpaceId();
    if (!spaceId) {
      this.toast('No parking space selected.');
      return;
    }
    const operatorId = this.operatorId();
    const companyName = this.companyName().trim();
    if (!companyName) {
      this.toast('Enter a company name.');
      return;
    }

    // Keep only members that have at least one plate and a name.
    const rows = this.members()
      .map(m => ({ ...m, parsedPlates: this.parsePlates(m.plates) }))
      .filter(m => m.parsedPlates.length > 0 && (m.firstName.trim() || m.lastName.trim()));
    if (rows.length === 0) {
      this.toast('Add at least one member with a name and a license plate.');
      return;
    }

    // Entered emails are resolved before anything is written. Checked up front rather than letting
    // the create fail: by then the organization may already exist and earlier members of the same
    // batch are already registered, leaving the operator to work out how far it got.
    const emails = await this.resolveEmails(rows);
    if (emails.error) {
      this.toast(emails.error);
      return;
    }

    this.isSubmitting.set(true);
    try {
      // 1) Resolve the organization (reuse the matched one, else create it).
      const orgId = await this.resolveOrganization(companyName);
      if (!orgId) throw new Error('Could not resolve organization id.');

      // 2) Ensure a long-term parking type exists for this space (once).
      const parkingTypeId = await this.ensureLongTermType(spaceId, operatorId);

      // 3) Create each member as a registered parker + plates + membership + right (+ classification).
      let created = 0;
      for (const m of rows) {
        const existingParkerId = emails.existingParkerIds.get(m.email.trim().toLowerCase()) ?? null;
        await this.registerMember(m, m.parsedPlates, orgId, parkingTypeId, spaceId, operatorId, existingParkerId);
        created++;
      }

      this.toast(`Registered ${created} member${created === 1 ? '' : 's'} for ${companyName}.`);
      this.resetForm();
      this.registered.emit();
    } catch (e) {
      console.error('Company registration failed:', e);
      this.toast(`Registration failed. ${describeHttpError(e)}`);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  /**
   * Resolves the emails the operator entered: an `error` when the form itself is wrong, otherwise
   * the parkers those emails already belong to.
   *
   * An email already on a parker means that person is in the system, so the row adds plates to
   * them instead of creating a second account on the same address — which is what the old
   * block-and-redirect did, with nowhere to redirect to, so operators left the email blank and got
   * a made-up `@company.jjames.local` duplicate of a real person.
   *
   * A duplicate *within the form* is still a typo the operator can correct on the spot.
   *
   * The lookup is an early convenience rather than the guard — `CreateRegisteredParker` refuses a
   * duplicate server-side regardless. That is why a failed or inconclusive lookup lets the submit
   * proceed: the authoritative check still runs, and blocking a legitimate registration on a
   * transient error would cost more than it protects.
   */
  private async resolveEmails(
    rows: { email: string }[],
  ): Promise<{ error: string | null; existingParkerIds: Map<string, string> }> {
    const existingParkerIds = new Map<string, string>();
    const entered = rows
      .map(r => r.email.trim().toLowerCase())
      .filter(email => email.length > 0);

    const seen = new Set<string>();
    for (const email of entered) {
      if (seen.has(email)) {
        return { error: `${email} is entered twice. Each member needs their own email address.`, existingParkerIds };
      }
      seen.add(email);
    }

    for (const email of seen) {
      try {
        // The backend filters email with Contains, so this returns anything the address is a
        // substring of. Page for a few and compare exactly, case-insensitively: taking the first
        // hit at face value would block a legitimate "max@jku.at" because "max@jku.at.invalid"
        // happens to exist.
        const existing = await firstValueFrom(
          this.registeredService.operatorRegisteredParkerFilterPost({
            email, pageIndex: 0, pageSize: 20, includeDeleted: false
          }));
        const match = (existing?.items ?? [])
          .find(p => (p.email ?? '').trim().toLowerCase() === email);
        if (match?.id) {
          existingParkerIds.set(email, match.id);
        }
      } catch (e) {
        // Deliberately not blocking — see the remark above.
        console.error(`Could not check whether ${email} is already registered:`, e);
      }
    }

    return { error: null, existingParkerIds };
  }

  /** Reuse the selected org, otherwise create a new one (operator as the acting admin). */
  private async resolveOrganization(companyName: string): Promise<string | undefined> {
    const existing = this.matchedOrg();
    if (existing?.id) return existing.id;

    // Re-check by name immediately before creating.
    //
    // `matchedOrg` is set by a *debounced* search, so submitting quickly — or pasting the name
    // and hitting register — leaves it null even when the company already exists, and a second
    // organization is created under the same name. This lookup is not debounced, so it closes
    // that race and is the reason duplicate companies appeared.
    const lookup = await firstValueFrom(this.orgService.organizationsFilterPost({
      name: companyName, pageIndex: 0, pageSize: 10, includeDeleted: false
    } as ListOrganizationRequest));
    const exact = (lookup?.items ?? []).find(
      o => (o.name ?? '').trim().toLowerCase() === companyName.toLowerCase());
    if (exact?.id) {
      // selectOrg, not matchedOrg.set: if the registration then fails, the form stays on screen
      // with this company selected and would otherwise claim it has no members.
      this.selectOrg(exact);
      return exact.id;
    }

    // contactPerson/contactPhone are new Organization fields — declared regen-safe here so this
    // compiles against the not-yet-regenerated API client (mirrors the existing operatorId pattern).
    const request: CreateOrganizationRequest & { contactPerson?: string | null; contactPhone?: string | null } = {
      name: companyName,
      description: null,
      contactPerson: this.contactPerson().trim() || null,
      contactPhone: this.contactPhone().trim() || null,
      billingAddress: {
        firstAddressLine: this.addressLine().trim() || null,
        locality: this.addressLocality().trim() || null,
        postCode: this.addressPostCode().trim() || null,
        // Always a valid 2-letter ISO code (backend requires it even for an empty address).
        countryCode: this.addressCountry() || DEFAULT_COUNTRY_CODE,
      },
      billingConfiguration: {},
      parkerRoles: null,
    };
    const created = await firstValueFrom(this.orgService.organizationsPost(request));
    return created?.id;
  }

  private async registerMember(
    member: MemberRow,
    plates: string[],
    orgId: string,
    parkingTypeId: string,
    spaceId: string,
    operatorId: string | null,
    existingParkerId: string | null,
  ): Promise<void> {
    // 3a) Create the registered parker — or reuse the one the entered email already belongs to.
    // Prefer the address the operator entered — it is what the person's own login is matched on,
    // and therefore the only way this record ever reaches them. The generated address is a fallback
    // for when it simply is not known at the counter.
    let parkerId = existingParkerId;
    if (!parkerId) {
      const email = member.email.trim() || this.buildTempEmail(plates[0], this.companyName());
      const parkerRequest: CreatedRegisteredParkerRequest = {
        email,
        firstName: member.firstName.trim() || this.companyName().trim(),
        lastName: member.lastName.trim() || 'Member',
        defaultPassword: this.generatePassword(),
        credits: [],
      };
      const parker = await firstValueFrom(this.registeredService.operatorRegisteredParkerPost(parkerRequest));
      parkerId = parker?.id ?? null;
      if (!parkerId) throw new Error('Registered parker creation returned no id.');
    }

    // 3b) Register + assign each plate. A plate the parker already holds is not assigned again.
    const plateIds: string[] = [];
    const plateIdByNumber = new Map<string, string>();
    for (const raw of plates) {
      const licensePlate = await firstValueFrom(this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: raw }));
      if (licensePlate?.id) {
        plateIds.push(licensePlate.id);
        plateIdByNumber.set(licensePlate.id, raw);
        if (existingParkerId && (licensePlate.parkersIDs ?? []).includes(parkerId)) continue;
        // Assigning also attaches the plate to the parker's own pool right (Attach sync), so for an
        // existing parker with a right there is nothing more to do for the plate below.
        await firstValueFrom(
          this.registeredService.operatorRegisteredParkerRegisteredParkerIdAssignLicensePlateLicensePlateIdPost(parkerId, licensePlate.id),
        );
      }
    }

    // 3c) Add the member to the organization (an existing member stays as they are).
    if (!existingParkerId || !this.orgMembers().some(m => m.parkerId === parkerId)) {
      try {
        await firstValueFrom(this.orgService.organizationsOrganizationIdParkersPost(orgId, { parkerId, role: OrganizationMemberRole.Member }));
      } catch (e) {
        // orgMembers only covers the company selected when the form was opened; the backend refuses a
        // live duplicate with "already associated", which for an existing parker is the goal state.
        if (!existingParkerId || !/already associated/i.test(describeHttpError(e))) throw e;
      }
    }

    // 3d) Set the site-scoped classification (like the webshop SSO does). Every type gets one,
    // suppliers included — the hotline registry filters on this record, so skipping it here is
    // what left the Supplier filter matching nothing.
    // An existing parker keeps theirs: the form's registration type describes the new plates, not
    // the person, and the call is an upsert that would overwrite e.g. a Student with a Supplier.
    // The current generated request model is stale (parkingOperatorId) — the backend expects
    // parkingSpaceId. Build the correct body; it aligns once the API client is regenerated.
    if (!existingParkerId) {
      const body = {
        parkingSpaceId: spaceId,
        classification: this.classificationForType(),
        validFrom: new Date().toISOString(),
        validTo: null,
        source: ClassificationSource.OperatorManual,
      };
      await firstValueFrom(this.classificationService.operatorParkerParkerIdSiteClassificationPost(parkerId, body as never));
    }

    // 3e) An existing parker with a right at this site gets the plates on that right — never a
    // second right beside it. Create the right only when they have none here.
    if (existingParkerId && await this.platesAreOnExistingRight(parkerId, spaceId, plateIds, plateIdByNumber)) return;

    // 3f) Create the long-term parking right (NOT pool) for this member.
    const validTo = this.validTo() ? new Date(this.validTo()).toISOString() : null;
    const typeDto: TimeBasedParkingRightDto = {
      type: ParkingRightTypeEnum.TimeBased,
      validFrom: null,
      validTo,
      startTime: null,
      endTime: null,
      validDays: null,
      isAllDay: true,
      isRecurring: false,
    };
    const zoneIds = this.selectedZoneIds();
    const request: CreateParkingRightRequest & { operatorId?: string | null } = {
      typeDto,
      parkingTypeId,
      allowedParkingSpaces: [spaceId],
      allowedLicensePlates: plateIds,
      allowedParkingZones: zoneIds.length ? zoneIds : null,
      purchasedByParker: parkerId,
      purchasedByOrganizationId: orgId,
      paymentReferenceId: null,
      name: this.companyName().trim(),
      comment: this.buildComment(member),
      operatorId,
    };
    await firstValueFrom(this.parkingRightService.parkingRightsPost(request));
  }

  /**
   * True when the parker already has a live right at this site and every plate is on it; false when
   * they have none here, so the caller creates one.
   *
   * Throws when they have a right that does not carry a plate. The assignment's Attach sync only
   * reaches rights hanging off a pool config, so an older right can be left behind, and creating a
   * second right next to it would give one person two entitlements — fixing the right is the
   * operator's call (Edit in the registry), not something to guess here. One request for the whole
   * parker, not one per plate.
   */
  private async platesAreOnExistingRight(
    parkerId: string,
    spaceId: string,
    plateIds: string[],
    plateNumberById: Map<string, string>,
  ): Promise<boolean> {
    const rights = await firstValueFrom(
      this.parkingRightService.parkingRightsByParkerParkerIdGet(parkerId, false, true));
    const rightsHere = (rights ?? []).filter(r =>
      !r.isDeleted && (r.allowedParkingSpaces ?? []).some(s => s.parkingSpaceId === spaceId));
    if (rightsHere.length === 0) return false;

    const covered = new Set(rightsHere.flatMap(r => (r.allowedLicensePlates ?? []).map(l => l.licenseId)));
    const missing = plateIds.filter(id => !covered.has(id));
    if (missing.length > 0) {
      const numbers = missing.map(id => plateNumberById.get(id) ?? id).join(', ');
      throw new Error(`${numbers} linked to the account but not on its existing parking right. Add it with Edit in the registry.`);
    }
    return true;
  }

  /** Find-or-create the space's long-term parking type, owned by the acting operator. */
  private async ensureLongTermType(spaceId: string, operatorId: string | null): Promise<string> {
    const listRequest: ListLongTermParkersRequest = {
      availableForParkingSpaces: [spaceId],
      operatorId: operatorId ?? undefined,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 1,
    };
    const existing = await firstValueFrom(this.parkingTypeService.parkingTypesLongTermParkersFilterPost(listRequest));
    const existingTypeId = existing?.items?.at(0)?.parkingTypeId;
    if (existingTypeId) return existingTypeId;

    const createRequest: CreateLongTermParkersRequest = { availableForParkingSpaces: [spaceId] };
    const created = await firstValueFrom(this.parkingTypeService.parkingTypesLongTermParkersPost(operatorId ?? '', createRequest));
    const createdTypeId = created?.parkingTypeId;
    if (!createdTypeId) throw new Error('Long-term parking type creation returned no parkingTypeId.');
    return createdTypeId;
  }

  // ---- Helpers --------------------------------------------------------------
  /** Maps the picked registration type onto the enum the backend stores. */
  private classificationForType(): ParkerClassification {
    switch (this.registrationType()) {
      case 'Employee': return ParkerClassification.Employee;
      case 'Student': return ParkerClassification.Student;
      case 'Supplier': return ParkerClassification.Supplier;
    }
  }

  private parsePlates(raw: string): string[] {
    return raw
      .split(/[\s,;\n]+/)
      .map(p => p.trim().toUpperCase().replace(/[-_]/g, ''))
      .filter(p => p.length > 0);
  }

  private buildTempEmail(plate: string, company: string): string {
    const plateSlug = plate.toLowerCase().replace(/[^a-z0-9]/g, '');
    const companySlug = this.slug(company) || 'company';
    return `${plateSlug || 'plate'}.${companySlug}@company.jjames.local`;
  }

  private slug(value: string): string {
    return value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  }

  private generatePassword(): string {
    // Random temp password; members don't log in (RequirePasswordChange stays on server-side).
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const rand = crypto.getRandomValues(new Uint32Array(16));
    return 'Jj1!' + Array.from(rand, n => chars[n % chars.length]).join('');
  }

  private buildComment(member: MemberRow): string | null {
    const parts: string[] = [];
    if (this.usesAkNumber() && member.akNumber.trim()) parts.push(`AK: ${member.akNumber.trim()}`);
    if (member.phone.trim()) parts.push(`Tel: ${member.phone.trim()}`);
    parts.push(this.registrationType());
    return parts.length ? parts.join(' · ') : null;
  }

  /** Full reset after a successful registration so the next entry starts clean. */
  private resetForm(): void {
    // Locked Employee/Student registrations stay locked to their organization after a reset —
    // clearing it here would leave the field showing empty instead of the fixed company.
    const org = this.linkedOrg();
    if (this.usesAkNumber() && org) {
      this.companyName.set(org.name ?? '');
      this.selectOrg(org);
    } else {
      this.companyName.set('');
      this.selectOrg(null);
    }
    this.companyResults.set([]);
    this.contactPerson.set('');
    this.contactPhone.set('');
    this.addressLine.set('');
    this.addressLocality.set('');
    this.addressPostCode.set('');
    this.addressCountry.set('AT');
    this.validTo.set('');
    this.selectedZoneIds.set([]);
    this.members.set([emptyMember()]);
  }

  private toast(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 3500 });
  }
}
